import { useCallback, useState, useContext } from 'react';
import useResizeObserver from '@react-hook/resize-observer';
import { DSTabsContext } from '../../DSTabsCTX.js';
import { isElementVisibleCarousel } from '../../utils/helpers.js';
import { useCarouselCallbacks } from './useCarouselCallbacks.js';
import type { DSTabsInternalsT } from '../../sharedTypes.js';

interface UseCarouselReturnTypeT {
  showChevrons: DSTabsInternalsT.ShowChevronsT;
  leftButtonOnClick: () => void;
  rightButtonOnClick: () => void;
  handleOnClick: (e: React.MouseEvent) => void;
  handleOnScroll: (e: React.UIEvent) => void;
}

export const useCarousel = (): UseCarouselReturnTypeT => {
  const { carouselOnlyListRef } = useContext(DSTabsContext);

  const [showChevrons, setShowChevrons] = useState<DSTabsInternalsT.ShowChevronsT>({
    right: false,
    left: false,
  });

  const { tabsRefAsArray } = useContext(DSTabsContext);

  const shouldShowLeft = useCallback(
    () => (carouselOnlyListRef.current ? carouselOnlyListRef.current.scrollLeft > 0 : false),

    [carouselOnlyListRef],
  );

  const shouldShowRight = useCallback(
    () =>
      carouselOnlyListRef.current
        ? carouselOnlyListRef.current.scrollLeft <
          carouselOnlyListRef.current.scrollWidth - carouselOnlyListRef.current.clientWidth
        : false,
    [carouselOnlyListRef],
  );

  const recalcChevrons = useCallback(() => {
    setShowChevrons({ right: shouldShowRight(), left: shouldShowLeft() });
  }, [shouldShowLeft, shouldShowRight]);

  const getVisibleItemsOffset = useCallback(() => {
    if (tabsRefAsArray.current) {
      const scroll = tabsRefAsArray.current.reduce((acc, c) => {
        if (isElementVisibleCarousel(c, carouselOnlyListRef.current)) {
          const styles = window.getComputedStyle(c);
          const width = c.clientWidth;
          const margin = parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);

          return acc + (width + margin);
        }
        return acc;
      }, 0);

      return scroll - 16;
    }
    return 0;
  }, [tabsRefAsArray, carouselOnlyListRef]);

  useResizeObserver(carouselOnlyListRef, recalcChevrons);

  const { leftButtonOnClick, rightButtonOnClick, handleOnClick, handleOnScroll } = useCarouselCallbacks({
    getVisibleItemsOffset,
    recalcChevrons,
  });

  return {
    showChevrons,
    leftButtonOnClick,
    rightButtonOnClick,
    handleOnClick,
    handleOnScroll,
  };
};
